import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Package, Plus, Edit, Search, Calendar, Upload, FileSpreadsheet, Download, AlertCircle, CheckCircle, X, Trash2 } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OrdersManagement = () => {
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [clientFilter, setClientFilter] = useState('all');
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkStatus, setBulkStatus] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkOrders, setBulkOrders] = useState('');
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    orderId: '',
    userId: '',
    productName: '',
    price: '',
    status: 'Picked Up',
    trackingId: '',
    storeName: '',
    dispatchDate: new Date().toISOString().slice(0, 16)
  });

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/admin/orders`);
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  useEffect(() => {
    fetchOrders();
    fetchUsers();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingOrder) {
        await axios.put(`${API}/admin/orders/${editingOrder._id || editingOrder.id}`, formData);
        toast({ title: '✅ Success', description: 'Order updated successfully' });
      } else {
        await axios.post(`${API}/admin/orders`, formData);
        toast({ title: '✅ Success', description: 'Order created successfully' });
      }
      setDialogOpen(false);
      setEditingOrder(null);
      resetForm();
      fetchOrders();
    } catch (error) {
      toast({ title: '❌ Error', description: error.response?.data?.error || 'Operation failed', variant: 'destructive' });
    }
  };

  const resetForm = () => {
    setFormData({
      orderId: '',
      userId: '',
      productName: '',
      price: '',
      status: 'Picked Up',
      trackingId: '',
      storeName: '',
      dispatchDate: new Date().toISOString().slice(0, 16)
    });
  };

  const handleEdit = (order) => {
    setEditingOrder(order);
    setFormData({
      orderId: order.orderId,
      userId: order.userId,
      productName: order.productName,
      price: order.price,
      status: order.status,
      trackingId: order.trackingId,
      storeName: order.storeName || '',
      dispatchDate: new Date(order.dispatchDate).toISOString().slice(0, 16)
    });
    setDialogOpen(true);
  };

  // CSV/Excel parsing
  const parseCSV = (text) => {
    const lines = text.split('\n').filter(line => line.trim());
    if (lines.length < 2) return [];
    
    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
    const orders = [];
    
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim());
      const order = {};
      
      headers.forEach((header, idx) => {
        const value = values[idx] || '';
        // Map common header names
        if (header.includes('client') || header.includes('user')) order.clientId = value;
        else if (header.includes('order') && header.includes('id')) order.orderId = value;
        else if (header.includes('product')) order.productName = value;
        else if (header.includes('price') || header.includes('amount')) order.price = value;
        else if (header.includes('status')) order.status = value;
        else if (header.includes('tracking')) order.trackingId = value;
        else if (header.includes('store')) order.storeName = value;
        else if (header.includes('date') || header.includes('dispatch')) order.dispatchDate = value;
      });
      
      if (order.clientId || order.productName) {
        orders.push(order);
      }
    }
    
    return orders;
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const parsedOrders = parseCSV(text);
      
      // Convert to text format for preview
      const previewText = parsedOrders.map((order, idx) => 
        `${order.clientId || ''},${order.orderId || ''},${order.productName || ''},${order.price || ''},${order.status || 'Picked Up'},${order.trackingId || ''},${order.storeName || ''},${order.dispatchDate || ''}`
      ).join('\n');
      
      setBulkOrders(`clientId,orderId,productName,price,status,trackingId,storeName,dispatchDate\n${previewText}`);
      toast({ title: '📁 File Loaded', description: `${parsedOrders.length} orders parsed from file` });
    };
    reader.readAsText(file);
  };

  const handleBulkUpload = async () => {
    setBulkLoading(true);
    setBulkResult(null);
    
    try {
      const parsedOrders = parseCSV(bulkOrders);
      
      if (parsedOrders.length === 0) {
        toast({ title: '❌ Error', description: 'No valid orders found', variant: 'destructive' });
        setBulkLoading(false);
        return;
      }
      
      const response = await axios.post(`${API}/admin/orders/bulk`, { orders: parsedOrders });
      
      setBulkResult(response.data);
      toast({ 
        title: '✅ Bulk Upload Complete', 
        description: `${response.data.created} orders created successfully` 
      });
      fetchOrders();
    } catch (error) {
      toast({ 
        title: '❌ Error', 
        description: error.response?.data?.detail || 'Bulk upload failed', 
        variant: 'destructive' 
      });
    } finally {
      setBulkLoading(false);
    }
  };

  const downloadTemplate = () => {
    const template = `clientId,orderId,productName,price,status,trackingId,storeName,dispatchDate
1001,ORD-001,Product Name,999,Picked Up,TRK123456,Store Name,2025-01-15
1002,ORD-002,Another Product,1499,In Transit,TRK789012,My Store,2025-01-16`;
    
    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bulk_orders_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleDelete = async (order) => {
    if (!window.confirm(`Delete order ${order.orderId || ''}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/orders/${order.id || order._id}`);
      toast({ title: '🗑️ Deleted', description: 'Order removed' });
      setSelectedIds((prev) => prev.filter((id) => id !== (order.id || order._id)));
      fetchOrders();
    } catch (error) {
      toast({ title: '❌ Error', description: error.response?.data?.detail || 'Delete failed', variant: 'destructive' });
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const applyBulkStatus = async () => {
    if (!bulkStatus || selectedIds.length === 0) return;
    try {
      await axios.patch(`${API}/admin/orders/bulk-update`, { orderIds: selectedIds, updates: { status: bulkStatus } });
      toast({ title: '✅ Updated', description: `${selectedIds.length} orders set to ${bulkStatus}` });
      setSelectedIds([]); setBulkStatus('');
      fetchOrders();
    } catch (error) {
      toast({ title: '❌ Error', description: error.response?.data?.detail || 'Bulk update failed', variant: 'destructive' });
    }
  };

  const deleteSelected = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Delete ${selectedIds.length} selected orders? This cannot be undone.`)) return;
    try {
      await axios.post(`${API}/admin/orders/bulk-delete`, { orderIds: selectedIds });
      toast({ title: '🗑️ Deleted', description: `${selectedIds.length} orders removed` });
      setSelectedIds([]);
      fetchOrders();
    } catch (error) {
      toast({ title: '❌ Error', description: error.response?.data?.detail || 'Bulk delete failed', variant: 'destructive' });
    }
  };

  const filteredOrders = orders.filter(order => {
    const search = searchTerm.toLowerCase();
    const matchesSearch = search === '' || 
      order.orderId?.toLowerCase().includes(search) ||
      order.productName?.toLowerCase().includes(search) ||
      order.clientId?.toString().includes(search) ||
      order.trackingId?.toLowerCase().includes(search);
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    const matchesClient = clientFilter === 'all' || order.userId === clientFilter;
    return matchesSearch && matchesStatus && matchesClient;
  });

  const allFilteredSelected = filteredOrders.length > 0 && filteredOrders.every((o) => selectedIds.includes(o.id || o._id));
  const toggleSelectAll = () => {
    if (allFilteredSelected) setSelectedIds([]);
    else setSelectedIds(filteredOrders.map((o) => o.id || o._id));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Delivered': return 'from-green-500 to-emerald-500';
      case 'In Transit': return 'from-blue-500 to-blue-500';
      case 'Picked Up': return 'from-blue-500 to-blue-500';
      case '1st Attempt': return 'from-yellow-500 to-blue-500';
      case '2nd Attempt': return 'from-orange-500 to-red-500';
      case 'RTO': return 'from-red-500 to-rose-500';
      default: return 'from-gray-500 to-gray-600';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">Orders Management</h2>
        <div className="flex items-center space-x-3">
          {/* Bulk Upload Button */}
          <Dialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                onClick={() => { setBulkOrders(''); setBulkResult(null); }}
                className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 shadow-lg hover:shadow-green-500/50 transition-all hover:scale-105"
              >
                <Upload className="w-4 h-4 mr-2" />
                Bulk Upload
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-slate-200 text-slate-800 max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-2xl text-blue-600 flex items-center">
                  <FileSpreadsheet className="w-6 h-6 mr-2 text-green-600" />
                  Bulk Order Upload
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4">
                {/* Instructions */}
                <Card className="bg-blue-500/10 border-blue-500/30">
                  <CardContent className="pt-4">
                    <h4 className="font-semibold text-blue-400 mb-2">📋 Instructions:</h4>
                    <ul className="text-sm text-slate-600 space-y-1">
                      <li>• Upload CSV file or paste data below</li>
                      <li>• Required columns: <span className="text-green-600">clientId, productName, price</span></li>
                      <li>• Optional columns: orderId, status, trackingId, storeName, dispatchDate</li>
                      <li>• Status options: Picked Up, In Transit, 1st Attempt, 2nd Attempt, Delivered, RTO</li>
                    </ul>
                  </CardContent>
                </Card>

                {/* File Upload & Template Download */}
                <div className="flex items-center space-x-4">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.txt"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                  <Button
                    onClick={() => fileInputRef.current?.click()}
                    variant="outline"
                    className="border-slate-200 hover:bg-slate-100"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Upload CSV File
                  </Button>
                  <Button
                    onClick={downloadTemplate}
                    variant="outline"
                    className="border-slate-200 hover:bg-slate-100"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download Template
                  </Button>
                </div>

                {/* Data Input */}
                <div>
                  <Label className="text-slate-600 font-medium">Order Data (CSV Format)</Label>
                  <Textarea
                    value={bulkOrders}
                    onChange={(e) => setBulkOrders(e.target.value)}
                    placeholder={`clientId,orderId,productName,price,status,trackingId,storeName,dispatchDate
1001,ORD-001,Product Name,999,Picked Up,TRK123,Store,2025-01-15
1002,ORD-002,Another Product,1499,In Transit,TRK456,Store,2025-01-16`}
                    className="bg-slate-50 border-slate-200 text-slate-800 h-48 font-mono text-sm mt-2"
                  />
                </div>

                {/* Result */}
                {bulkResult && (
                  <Card className={`${bulkResult.errors?.length > 0 ? 'bg-yellow-500/10 border-yellow-500/30' : 'bg-green-500/10 border-green-200'}`}>
                    <CardContent className="pt-4">
                      <div className="flex items-center space-x-2 mb-3">
                        {bulkResult.errors?.length > 0 ? (
                          <AlertCircle className="w-5 h-5 text-yellow-400" />
                        ) : (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        )}
                        <h4 className="font-semibold text-slate-800">{bulkResult.message}</h4>
                      </div>
                      <p className="text-green-600 font-medium">✅ {bulkResult.created} orders created successfully</p>
                      {bulkResult.errors?.length > 0 && (
                        <div className="mt-3 p-3 bg-red-500/10 rounded border border-red-500/30">
                          <p className="text-red-400 font-medium mb-2">⚠️ Errors:</p>
                          <ul className="text-sm text-red-300 space-y-1">
                            {bulkResult.errors.map((err, idx) => (
                              <li key={idx}>• {err}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Submit Button */}
                <Button
                  onClick={handleBulkUpload}
                  disabled={bulkLoading || !bulkOrders.trim()}
                  className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 shadow-lg hover:shadow-green-500/50 transition-all py-6 text-lg font-bold"
                >
                  {bulkLoading ? (
                    <div className="flex items-center">
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                      Processing...
                    </div>
                  ) : (
                    <>
                      <Upload className="w-5 h-5 mr-2" />
                      Upload Orders
                    </>
                  )}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Single Order Add Button */}
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => { resetForm(); setEditingOrder(null); }} className="bg-gradient-to-r from-blue-500 to-blue-500 hover:from-blue-600 hover:to-cyan-600 shadow-lg hover:shadow-blue-500/50 transition-all hover:scale-105">
                <Plus className="w-4 h-4 mr-2" />
                Add Order
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-slate-200 text-slate-800 max-w-2xl">
              <DialogHeader>
                <DialogTitle className="text-xl text-blue-400">{editingOrder ? 'Edit Order' : 'Create New Order'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-600 font-medium">Order ID</Label>
                    <Input
                      value={formData.orderId}
                      onChange={(e) => setFormData({ ...formData, orderId: e.target.value })}
                      placeholder="#10001"
                      required
                      className="bg-slate-50 border-slate-200 text-slate-800 mt-2"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-600 font-medium">User</Label>
                    <Select value={formData.userId} onValueChange={(value) => setFormData({ ...formData, userId: value })} required>
                      <SelectTrigger className="bg-slate-50 border-slate-200 text-slate-800 mt-2">
                        <SelectValue placeholder="Select user" />
                      </SelectTrigger>
                      <SelectContent>
                        {users.map(user => (
                          <SelectItem key={user.principalId} value={user.principalId}>
                            #{user.clientId} - {user.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-slate-600 font-medium">Product Name</Label>
                  <Input
                    value={formData.productName}
                    onChange={(e) => setFormData({ ...formData, productName: e.target.value })}
                    placeholder="Product name"
                    required
                    className="bg-slate-50 border-slate-200 text-slate-800 mt-2"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-600 font-medium">Price (₹)</Label>
                    <Input
                      type="number"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                      placeholder="1299"
                      required
                      className="bg-slate-50 border-slate-200 text-slate-800 mt-2"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-600 font-medium">Status</Label>
                    <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                      <SelectTrigger className="bg-slate-50 border-slate-200 text-slate-800 mt-2">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Picked Up">Picked Up</SelectItem>
                        <SelectItem value="In Transit">In Transit</SelectItem>
                        <SelectItem value="1st Attempt">1st Attempt</SelectItem>
                        <SelectItem value="2nd Attempt">2nd Attempt</SelectItem>
                        <SelectItem value="Delivered">Delivered</SelectItem>
                        <SelectItem value="RTO">RTO</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-600 font-medium">Tracking ID</Label>
                    <Input
                      value={formData.trackingId}
                      onChange={(e) => setFormData({ ...formData, trackingId: e.target.value })}
                      placeholder="TRK123456789"
                      className="bg-slate-50 border-slate-200 text-slate-800 mt-2"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-600 font-medium">Store Name</Label>
                    <Input
                      value={formData.storeName}
                      onChange={(e) => setFormData({ ...formData, storeName: e.target.value })}
                      placeholder="Store name"
                      className="bg-slate-50 border-slate-200 text-slate-800 mt-2"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-slate-600 font-medium">Dispatch Date</Label>
                  <Input
                    type="datetime-local"
                    value={formData.dispatchDate}
                    onChange={(e) => setFormData({ ...formData, dispatchDate: e.target.value })}
                    className="bg-slate-50 border-slate-200 text-slate-800 mt-2"
                  />
                </div>
                <Button type="submit" className="w-full bg-gradient-to-r from-blue-500 to-blue-500 hover:from-blue-600 hover:to-cyan-600 shadow-lg hover:shadow-blue-500/50 transition-all">
                  {editingOrder ? 'Update Order' : 'Create Order'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Search & Filter */}
      <Card className="bg-white shadow-lg border-slate-200">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-3 w-4 h-4 text-slate-500" />
              <Input
                placeholder="Search by Order ID, Product, Client ID, Tracking ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-white border-slate-200 text-slate-800 placeholder:text-slate-400"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48 bg-white border-slate-200 text-slate-800">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="Picked Up">Picked Up</SelectItem>
                <SelectItem value="In Transit">In Transit</SelectItem>
                <SelectItem value="1st Attempt">1st Attempt</SelectItem>
                <SelectItem value="2nd Attempt">2nd Attempt</SelectItem>
                <SelectItem value="Delivered">Delivered</SelectItem>
                <SelectItem value="RTO">RTO</SelectItem>
              </SelectContent>
            </Select>
            <Select value={clientFilter} onValueChange={(v) => { setClientFilter(v); setSelectedIds([]); }}>
              <SelectTrigger className="w-56 bg-white border-slate-200 text-slate-800" data-testid="orders-client-filter">
                <SelectValue placeholder="Filter by client" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Clients</SelectItem>
                {users.map((u) => (
                  <SelectItem key={u.principalId} value={u.principalId}>#{u.clientId} - {u.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Bulk action bar */}
          <div className="flex flex-wrap items-center gap-3 mt-4 pt-4 border-t border-slate-100">
            <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
              <input type="checkbox" checked={allFilteredSelected} onChange={toggleSelectAll} data-testid="orders-select-all" className="w-4 h-4 accent-blue-600" />
              Select all ({filteredOrders.length})
            </label>
            <span className="text-sm font-semibold text-blue-600">{selectedIds.length} selected</span>
            <div className="flex items-center gap-2 ml-auto flex-wrap">
              <Select value={bulkStatus} onValueChange={setBulkStatus}>
                <SelectTrigger className="w-44 bg-white border-slate-200 text-slate-800" data-testid="orders-bulk-status">
                  <SelectValue placeholder="Set status to..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Picked Up">Picked Up</SelectItem>
                  <SelectItem value="In Transit">In Transit</SelectItem>
                  <SelectItem value="1st Attempt">1st Attempt</SelectItem>
                  <SelectItem value="2nd Attempt">2nd Attempt</SelectItem>
                  <SelectItem value="Delivered">Delivered</SelectItem>
                  <SelectItem value="RTO">RTO</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={applyBulkStatus} disabled={selectedIds.length === 0 || !bulkStatus} data-testid="orders-bulk-apply" className="bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-40">
                Apply to Selected
              </Button>
              <Button onClick={deleteSelected} disabled={selectedIds.length === 0} data-testid="orders-bulk-delete" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50 disabled:opacity-40">
                <Trash2 className="w-4 h-4 mr-1" /> Delete Selected
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-500/10 to-blue-500/10 border-blue-500/30">
          <CardContent className="pt-6">
            <p className="text-sm text-slate-600 font-medium">Total Orders</p>
            <p className="text-2xl md:text-3xl font-bold text-blue-400 mt-2">{orders.length}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border-green-200">
          <CardContent className="pt-6">
            <p className="text-sm text-slate-600 font-medium">Delivered</p>
            <p className="text-2xl md:text-3xl font-bold text-green-600 mt-2">{orders.filter(o => o.status === 'Delivered').length}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-yellow-500/10 to-blue-500/10 border-yellow-500/30">
          <CardContent className="pt-6">
            <p className="text-sm text-slate-600 font-medium">In Transit</p>
            <p className="text-2xl md:text-3xl font-bold text-yellow-400 mt-2">{orders.filter(o => o.status === 'In Transit').length}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-red-500/10 to-rose-500/10 border-red-500/30">
          <CardContent className="pt-6">
            <p className="text-sm text-slate-600 font-medium">RTO</p>
            <p className="text-2xl md:text-3xl font-bold text-red-400 mt-2">{orders.filter(o => o.status === 'RTO').length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Orders List */}
      <div className="grid gap-4">
        {filteredOrders.length === 0 ? (
          <Card className="bg-white shadow-lg border-slate-200">
            <CardContent className="py-12 text-center">
              <Package className="w-16 h-16 mx-auto mb-4 text-slate-600" />
              <p className="text-slate-600">No orders found</p>
            </CardContent>
          </Card>
        ) : (
          filteredOrders.map(order => (
            <Card key={order.id || order.orderId} className="bg-white shadow-lg border-slate-200 hover:border-white/30 transition-all animate-in fade-in slide-in-from-bottom-2 group">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3 flex-1">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(order.id || order._id)}
                      onChange={() => toggleSelect(order.id || order._id)}
                      data-testid="order-checkbox"
                      className="w-4 h-4 mt-1 accent-blue-600 flex-shrink-0"
                    />
                    <div className="flex-1 grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div>
                      <p className="text-xs text-slate-500 font-medium mb-1">Order ID</p>
                      <p className="font-semibold text-blue-400">{order.orderId}</p>
                      <p className="text-xs text-slate-400 mt-1">Client #{order.clientId}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 font-medium mb-1">Product</p>
                      <p className="text-slate-800 font-medium">{order.productName}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 font-medium mb-1">Price</p>
                      <p className="text-green-600 font-bold text-lg">₹{order.price}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 font-medium mb-1">Status</p>
                      <span className={`inline-block px-3 py-1 rounded-full text-xs bg-gradient-to-r ${getStatusColor(order.status)} text-slate-800 font-medium`}>
                        {order.status}
                      </span>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 font-medium mb-1">Tracking</p>
                      <p className="text-slate-800 font-mono text-sm">{order.trackingId || '-'}</p>
                    </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="ghost" onClick={() => handleEdit(order)} className="hover:bg-slate-100 transition-all hover:scale-110">
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(order)} data-testid="order-delete-btn" className="hover:bg-red-50 text-red-500 transition-all hover:scale-110">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-slate-200 flex items-center justify-between text-xs text-slate-500">
                  <span>Store: {order.storeName || '-'}</span>
                  <span>Dispatch: {new Date(order.dispatchDate).toLocaleDateString()}</span>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default OrdersManagement;
